import { useState } from "react";
import { Plus, Pencil, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDate } from "../../lib/api";
import { PageHeader, Badge, SkeletonGrid, ErrorState, EmptyState } from "../../components/common";
import { AnimatedProgress } from "../../components/motion";
import { Field, Input, Select, Toolbar, Table, Row, Td, IconBtn, FormDialog, ConfirmDelete } from "../../components/admin/kit";

const ASG_STATUS = [["active", "Aktif"], ["completed", "Tamamlandı"], ["passive", "Pasif"]];
const today = () => new Date().toISOString().slice(0, 10);

export default function AdminAssignments() {
  const { data, loading, error, reload } = useApi("/admin/assignments");
  const students = useApi("/admin/students");
  const courses = useApi("/admin/courses");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => setForm({ user_id: students.data?.[0]?.id || "", course_ids: [], start_date: today(), end_date: "", status: "active" });
  const toggleCourse = (id) => setForm({ ...form, course_ids: form.course_ids.includes(id) ? form.course_ids.filter((x) => x !== id) : [...form.course_ids, id] });

  const save = async () => {
    if (!form.id && (!form.user_id || form.course_ids.length === 0)) return toast.error("Kursiyer ve en az bir eğitim seçin.");
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/assignments/${form.id}`, { start_date: form.start_date, end_date: form.end_date, status: form.status });
      else {
        const r = await api.post("/admin/assignments", form);
        toast.success(`${r.data.created} yeni atama yapıldı.`);
      }
      if (form.id) toast.success("Atama güncellendi.");
      setForm(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const del = async (a) => {
    await api.delete(`/admin/assignments/${a.id}`).then(() => { toast.success("Atama kaldırıldı."); reload(); }).catch((e) => toast.error(errMsg(e)));
  };
  const list = (data || []).filter((a) => `${a.student_name} ${a.course_title}`.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR")));

  return (
    <div>
      <PageHeader eyebrow="Yönetim" title="Eğitim Atama" desc="Kursiyerlere bir veya birden fazla eğitim atayın." />
      <Toolbar q={q} setQ={setQ} placeholder="Kursiyer veya eğitim ara..." action={
        <button onClick={openNew} className="btn-primary inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="add-assignment-btn"><Plus className="h-4 w-4" /> Yeni Atama</button>
      } />
      {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <SkeletonGrid count={1} className="h-80" cols="" /> : list.length === 0 ? (
        <EmptyState icon={Link2} title="Henüz atama yapılmadı." desc="Bir kursiyere eğitim atayarak başlayın." />
      ) : (
        <Table head={["Kursiyer", "Eğitim", "Tarih", "İlerleme", "Durum", ""]} testid="assignments-table">
          {list.map((a) => (
            <Row key={a.id} data-testid={`assignment-row-${a.id}`}>
              <Td className="font-semibold text-[var(--ink)]">{a.student_name}</Td>
              <Td className="text-slate-600">{a.course_title}</Td>
              <Td className="text-xs text-slate-500">{fmtDate(a.start_date)} – {fmtDate(a.end_date)}</Td>
              <Td><div className="flex w-36 items-center gap-2"><AnimatedProgress value={a.progress} height="h-1.5" /><span className="text-xs font-semibold">%{a.progress}</span></div></Td>
              <Td><Badge status={a.status} /></Td>
              <Td>
                <div className="flex justify-end gap-1">
                  <IconBtn icon={Pencil} label="Düzenle" onClick={() => setForm(a)} data-testid={`edit-assignment-${a.id}`} />
                  <ConfirmDelete label="Kaldır" onConfirm={() => del(a)} testid={`delete-assignment-${a.id}`} />
                </div>
              </Td>
            </Row>
          ))}
        </Table>
      )}
      <FormDialog open={!!form} onClose={() => setForm(null)} title={form?.id ? "Atamayı Düzenle" : "Yeni Eğitim Ataması"} onSubmit={save} saving={saving} testid="assignment-form-dialog">
        {form && (
          <>
            {form.id ? (
              <p className="rounded-xl bg-slate-50 p-3 text-sm"><b>{form.student_name}</b> · {form.course_title}</p>
            ) : (
              <>
                <Field label="Kursiyer">
                  <Select value={form.user_id} onChange={set("user_id")} options={(students.data || []).map((s) => [s.id, `${s.name}${s.email ? " – " + s.email : ""}`])} data-testid="assignment-student-select" />
                </Field>
                <div>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-500">Eğitimler</span>
                  <div className="max-h-52 space-y-1.5 overflow-y-auto rounded-xl border border-slate-200 p-2">
                    {(courses.data || []).filter((c) => c.status === "active").map((c) => (
                      <label key={c.id} className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${form.course_ids.includes(c.id) ? "bg-[var(--brand-50)]" : "hover:bg-slate-50"}`}>
                        <input type="checkbox" checked={form.course_ids.includes(c.id)} onChange={() => toggleCourse(c.id)} className="h-4 w-4 accent-[var(--brand)]" data-testid={`assignment-course-checkbox-${c.id}`} />
                        {c.title}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Başlangıç Tarihi"><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="assignment-start-input" /></Field>
              <Field label="Bitiş Tarihi"><Input type="date" value={form.end_date} onChange={set("end_date")} data-testid="assignment-end-input" /></Field>
            </div>
            <Field label="Durum"><Select value={form.status} onChange={set("status")} options={ASG_STATUS} data-testid="assignment-status-select" /></Field>
          </>
        )}
      </FormDialog>
    </div>
  );
}
